import {
  FooterDataDto,
  TableColumnConfigDto,
  WidgetDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  combineFormatters,
  dateFormatters,
  emptyArrayFormatter,
  emptyValueFormatter,
  numberFormatters,
  postfixFormatter,
  textFormatters,
} from '@asset-metrix/portal-ui';
import { ColDef } from 'ag-grid-community';

import i18n from '../../i18n/config';

import { getCellClassFunc } from './cellClasses';
import {
  CellRendererSelectorOptions,
  getCellRendererSelector,
} from './getCellRenderer';
import { getGroupFooterAggregationFunc } from './getGroupFooterAggregationFunc';

const rightAlignClasses = {
  headerClass: 'ag-right-aligned-header',
};

/* Only enable column resizing for columns that do not rely on
 * custom cell renderers like workflowVisualisation etc. */
const colTypesResizable = new Set<TableColumnConfigDto['type']>([
  'date',
  'file_name',
  'link',
  'number',
  'pageFilter',
  'text',
]);

export function configToColDef(
  col: TableColumnConfigDto,
  widgetId: WidgetDto['id'],
  isFooterLoading: boolean,
  columnsConfig: TableColumnConfigDto[],
  options?: CellRendererSelectorOptions
) {
  const fixedWidth = col.width != null;
  const userGroupSelection = options?.userGroupSelection;
  const userGroup = userGroupSelection?.find((ug) => ug.colId === col.id);

  return {
    /* Needed to correctly resize components with fixed width for tables
     * without horizontal scrolling. In this cases, sizeColumnsToFit will be
     * used to resize the table, which potentially could cut content from
     * cell renderers like the workflowVisualisation if it is not listed here */
    suppressSizeToFit:
      fixedWidth ||
      col.type === 'file_name' ||
      col.type === 'fileDownload' ||
      col.type === 'workflowVisualization' ||
      col.type === 'workflowActions' ||
      col.type === 'workflowProgress',
    autoHeight:
      col.wordwrap ||
      (col.type === 'file_name' && options?.showFullTextSearchResults),
    field: col.key,
    headerName: col.label,
    hide:
      !!col.hidden ||
      hideColumnWithEmptyGroupedData(col, isFooterLoading, options),
    rowGroup: Number.isInteger(userGroup?.level),
    pinned: !!col.freeze,
    resizable: colTypesResizable.has(col.type),
    sortable: col.sortable,
    suppressColumnsToolPanel: !!col.hidden,
    wrapText: col.wordwrap,
    valueFormatter: getValueFormatters(col),
    cellClass: getCellClassFunc(col, options?.rowIdToHighlight),
    ...(fixedWidth ? { width: col.width } : {}),
    ...(col.minWidth != null ? { minWidth: col.minWidth } : {}),
    // ...(fixedWidth ? { minWidth: col.width } : {}),
    // ...(fixedWidth ? { maxWidth: col.width } : {}),
    ...(col.align === 'right' ? rightAlignClasses : {}),
    // aggregation options
    ...(options?.footerData &&
      'groupFooter' in options.footerData && {
        aggFunc: getGroupFooterAggregationFunc(options.footerData.groupFooter),
      }),
    ...(userGroup && { rowGroupIndex: userGroup?.level }),
    // Grid columns inherit maxWidth 500. A fixed cap clips long workflow
    // visualizations, so this column stays unbounded and can grow with its steps.
    ...(col.type === 'workflowVisualization' && {
      maxWidth: Number.MAX_SAFE_INTEGER,
    }),
    ...(col.type === 'workflowActions' && { maxWidth: 2000 }),
    cellRendererSelector: !col.hidden
      ? getCellRendererSelector(
          col,
          widgetId,
          isFooterLoading && options?.footerData?.mainFooter?.[col.key] == null,
          columnsConfig,
          options
        )
      : undefined,
    headerComponentParams: { sublabel: col.sublabel },
    cellDataType: getCellDataType(col),
  } as ColDef;
}

export const formattersByType = {
  checkbox: [emptyValueFormatter],
  date: dateFormatters,
  file_name: [emptyValueFormatter],
  fileDownload: [emptyValueFormatter, emptyArrayFormatter],
  number: numberFormatters,
  text: textFormatters,
  link: [postfixFormatter, emptyValueFormatter],
  pageFilter: [postfixFormatter, emptyValueFormatter],
  workflowActions: [emptyArrayFormatter],
  workflowProgress: [emptyValueFormatter],
  workflowVisualization: [emptyValueFormatter],
};

const getValueFormatters = (columnConfig: TableColumnConfigDto) => {
  const formatters = formattersByType[columnConfig.type] ?? [];
  return combineFormatters(
    formatters,
    columnConfig.type === 'text'
      ? {
          trueLabel: i18n.t('app.label.true'),
          falseLabel: i18n.t('app.label.false'),
          ...columnConfig,
        }
      : columnConfig
  );
};

// see https://www.ag-grid.com/javascript-data-grid/cell-data-types/
// if no cellDataType is set, the column will automatically try to infer the type from the first row
// that can lead to wrong cell renderer types if the first row contains for example null values
// therefore, we explicitly set the type on text and checkbox columns
const getCellDataType = (columnConfig: TableColumnConfigDto) => {
  if (columnConfig.type === 'text') {
    return 'text';
  }
  if (columnConfig.type === 'checkbox') {
    return 'boolean';
  }

  return undefined;
};

const hasMeaningfulValue = (value: unknown) => value != null && value !== '';

const hasGroupedFooterData = (
  groupFooterData?: NonNullable<FooterDataDto['groupFooter']>[string]
): boolean => {
  if (groupFooterData == null) {
    return false;
  }

  if (hasMeaningfulValue(groupFooterData.total)) {
    return true;
  }

  return Object.values(groupFooterData.grouped ?? {}).some(
    hasGroupedFooterData
  );
};

const hasFooterData = (key: string, footerData?: FooterDataDto) => {
  const mainFooterData = footerData?.mainFooter?.[key];
  const groupFooterData = footerData?.groupFooter?.[key];

  if (hasMeaningfulValue(mainFooterData)) {
    return true;
  }

  if (hasGroupedFooterData(groupFooterData)) {
    return true;
  }

  return false;
};

// Hide columns without any grouped data (footer / grouped footer)
// - Only applies if the table is a grouped table
// - Does not apply if the widget is opened in fullscreen mode
// - Does not apply if a top-level group is expanded
function hideColumnWithEmptyGroupedData(
  columnConfig: TableColumnConfigDto,
  isFooterLoading: boolean,
  options?: CellRendererSelectorOptions
) {
  const { key, appearsInFooter } = columnConfig;

  // true for isGroupOnlyMode too
  if (!options?.isGroupedTable) {
    return false;
  }

  if (options?.isFullScreenMode) {
    return false;
  }

  // at least one group needs to be fully expanded
  if (options?.isSomeDataRowVisible) {
    return false;
  }

  if (isFooterLoading && appearsInFooter) {
    return false;
  }

  if (!hasFooterData(key, options?.footerData)) {
    return true;
  }

  return false;
}
